import moment from "moment/moment";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { addToast, toastMessageTypes } from "@/redux/actions/toaster";
import { paymentRequests } from "@/api/(requests)/payment";
import { usePaymentMethod } from "@/utils/hooks/usePatient";

import { CardOnFile } from "./CardOnFile";
import { PaymentHistory } from "./PaymentHistory";
import { PaymentModal } from "./PaymentModal";

import "./styles.scss";

const extractMostRecentPaymentMethodInformation = (paymentMethods) => {
  if (!paymentMethods) {
    return undefined;
  }

  const paymentMethod = paymentMethods.at(0);
  const cardEndsIn = paymentMethod?.last_four_digits;
  const expiresOn = moment(paymentMethod?.expiration_date, "YYYY-MM-DD HH:mm:ss");
  const dateAdded = moment.utc(paymentMethod?.timestamp, "YYYY-MM-DD HH:mm:ss").local();
  const hasCardExpired = moment().isAfter(expiresOn);

  return {
    cardEndsIn,
    dateAdded,
    expiresOn,
    hasCardExpired,
    hasCardOnFile: Boolean(paymentMethod),
  };
};

export const Payment = ({ patientDetails }) => {
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const paymentMethodsResult = usePaymentMethod(patientDetails.username);
  const paymentMethodInformation = extractMostRecentPaymentMethodInformation(paymentMethodsResult);
  const patientId = patientDetails.username;

  const [transactionHistoryResult, setTransactionHistory] = useState({
    items: [],
    last_evaluated_key: null,
  });

  useEffect(() => {
    const fetchInitialTransactionHistory = async () => {
      const result = await getTransactionHistory(patientId);

      setTransactionHistory(result);
    };

    fetchInitialTransactionHistory();
  }, [patientId]);

  const getTransactionHistory = async (patientId, lastEvaluatedKey = null) => {
    if (!patientId) {
      return { items: [], last_evaluated_key: null };
    }

    // Pass the lastEvaluatedKey to the API call
    const data = {
      last_evaluated_key: lastEvaluatedKey,
      patient_id: patientId,
    };

    try {
      return await paymentRequests.fetchTransactionHistory({
        data: data,
      });
    } catch (error) {
      console.error("Error fetching transaction history:", error);

      return { error: "Error", items: [], last_evaluated_key: null };
    }
  };

  const loadMore = async () => {
    try {
      if (!transactionHistoryResult || !transactionHistoryResult.last_evaluated_key) {
        return;
      }

      const result = await getTransactionHistory(
        patientId,
        transactionHistoryResult.last_evaluated_key,
      );

      setTransactionHistory((prevTransactionHistory) => ({
        items: [...(prevTransactionHistory?.items || []), ...result.items],
        last_evaluated_key: result.last_evaluated_key,
      }));
    } catch (error) {
      console.error("Error loading more transaction history:", error);
    }
  };

  const handleUpdateTransactionHistory = async () => {
    const result = await getTransactionHistory(patientId);

    if (result.error) {
      addToast({
        message: "Error getting payment history.",
        messageType: toastMessageTypes.error_v2,
      });
    }

    setTransactionHistory(result);
  };

  return (
    <>
      <CardOnFile
        cardEndsIn={paymentMethodInformation?.cardEndsIn}
        dateAdded={paymentMethodInformation?.dateAdded.format("MM/DD/YYYY")}
        expiresOn={paymentMethodInformation?.expiresOn.format("MM/YYYY")}
        hasCardExpired={paymentMethodInformation?.hasCardExpired}
        hasCardOnFile={paymentMethodInformation?.hasCardOnFile}
        hasLoaded={Boolean(paymentMethodsResult)}
      />

      <PaymentHistory
        hasCardOnFile={paymentMethodInformation?.hasCardOnFile}
        isCardExpired={paymentMethodInformation?.hasCardExpired}
        onLoadMore={loadMore}
        openPaymentModal={() => setIsPaymentModalOpen(true)}
        patientFirstName={patientDetails.first_name}
        patientId={patientDetails.username}
        transactionHistory={transactionHistoryResult}
      />

      <PaymentModal
        isOpen={isPaymentModalOpen}
        patientId={patientDetails.username}
        setIsOpen={setIsPaymentModalOpen}
        updateTransactionHistory={handleUpdateTransactionHistory}
      />
    </>
  );
};

Payment.propTypes = {
  patientDetails: PropTypes.object,
};
